import React, { forwardRef, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@purpur/button";
import type { BaseProps } from "@purpur/common-types";
import { Heading } from "@purpur/heading";
import { IconClose } from "@purpur/icon/close";
import { Paragraph } from "@purpur/paragraph";
import { useThemePortalContainer } from "@purpur/theme";
import { VisuallyHidden } from "@purpur/visually-hidden";
import {
  Close as RadixClose,
  Content as RadixContent,
  Description as RadixDescription,
  Overlay as RadixOverlay,
  Portal as RadixPortal,
  Title as RadixTitle,
} from "@radix-ui/react-dialog";
import c from "classnames/bind";

import "@purpur/notification/styles";
import styles from "./modal-content.module.scss";

export type DefaultProps = Omit<BaseProps, "children"> & {
  /**
   * One or several CTA links or buttons
   * */
  actions?: ReactNode;
  children: ReactNode;
  /**
   * An optional accessible description to be announced when the dialog is opened.
   * */
  description?: string;
  /**
   * Prevents the modal from closing on click outside if set to `true`.
   * */
  disableCloseOnClickOutside?: boolean;
  /**
   * Visually hides the description if set to `true`.
   * */
  hideDescription?: boolean;
  /**
   * An optional image to be displayed at the top of the modal.
   * */
  image?: ReactNode;
  /**
   * Provide a Purpur Notification which will render beneath the modal buttons, otherwise leave it undefined.
   * */
  notification?: ReactNode;
  /**
   * Determines whether the button group should be fixed at the bottom or scroll with the content.
   * */
  stickyButtons?: boolean;
  /**
   * An accessible title to be announced when the dialog is opened.
   * */
  title: string;
  /**
   * z-index of the modal
   * */
  zIndex?: number;
  /**
   * Optional DOM element to portal the modal content into. When omitted,
   * the modal renders inside the nearest `ThemeProvider` so it inherits
   * the active color scheme. Pass `document.body` (or any other element) to
   * override. Pass `null` to fall back to Radix' default (`document.body`).
   * */
  container?: HTMLElement | null;
  /**
   * Event handler called when focus moves to the trigger after closing.
   * */
  onCloseAutoFocus?: (e: Event) => void;
};

type NoCloseButtonProps = {
  /**
   * An accessible label for the close button.
   * */
  closeButtonAriaLabel?: never;
  /**
   * Shows the close button if set to `true`. Must be used in conjunction with `closeButtonAriaLabel`.
   * */
  showCloseButton?: false;
};

type CloseButtonProps = {
  /**
   * An accessible label for the close button.
   * */
  closeButtonAriaLabel: string;
  /**
   * Shows the close button if set to `true`. Must be used in conjunction with `closeButtonAriaLabel`.
   * */
  showCloseButton: true;
};

export type ModalContentProps = DefaultProps & (CloseButtonProps | NoCloseButtonProps);

const hasCloseButtonProps = (
  props: CloseButtonProps | NoCloseButtonProps
): props is CloseButtonProps => (props as CloseButtonProps).showCloseButton === true;

const cx = c.bind(styles);
const rootClassName = "purpur-modal-content";

export const ModalContent = forwardRef<HTMLDivElement, ModalContentProps>(
  (
    {
      ["data-testid"]: dataTestId,
      actions,
      children,
      className,
      description,
      disableCloseOnClickOutside,
      hideDescription,
      image,
      stickyButtons = true,
      title,
      notification = undefined,
      zIndex,
      container,
      onCloseAutoFocus,
      ...props
    },
    ref
  ) => {
    const { closeButtonAriaLabel, showCloseButton, ...rest } = props;
    const [contentOverflow, setContentOverflow] = useState(false);
    const [scrollbarWidth, setScrollbarWidth] = useState(0);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const wrapperInnerRef = useRef<HTMLDivElement>(null);
    const themePortalContainer = useThemePortalContainer();
    const portalContainer =
      container === undefined ? themePortalContainer : container ?? undefined;

    const classes = cx(
      rootClassName,
      { [`${rootClassName}--with-image`]: !!image },
      { [`${rootClassName}--overflow`]: contentOverflow },
      { [`${rootClassName}--sticky-footer`]: stickyButtons },
      className
    );

    const getTestId = (id: string) => (dataTestId ? `${dataTestId} ${id}` : undefined);

    const handlePointerDownOutside = (event: CustomEvent<{ originalEvent: PointerEvent }>) => {
      if (disableCloseOnClickOutside) {
        event.preventDefault();
      }
    };

    const handleContentOverflow = useCallback(() => {
      if (!wrapperRef.current || !wrapperInnerRef.current) {
        return;
      }

      const computedStyle = window.getComputedStyle(wrapperRef.current, null);
      const wrapperHeight = parseFloat(computedStyle.getPropertyValue("height"));
      const innerHeight = wrapperInnerRef.current.offsetHeight;

      setContentOverflow(wrapperHeight < innerHeight);

      setScrollbarWidth(
        (wrapperRef.current.offsetWidth ?? 0) - (wrapperRef.current.clientWidth ?? 0)
      );
    }, []);

    const handleInitialFocus = () => {
      const frame = wrapperInnerRef.current;
      if (frame) {
        const heading = frame.querySelector("h2");

        heading &&
          setTimeout(() => {
            heading.focus();
          });
      }
    };

    const handleEscapeKeyDown = (event: KeyboardEvent) => {
      if (disableCloseOnClickOutside && !showCloseButton) {
        event.preventDefault();
      }
    };

    useEffect(() => {
      window.addEventListener("resize", handleContentOverflow);

      return () => {
        window.removeEventListener("resize", handleContentOverflow);
      };
    }, [handleContentOverflow]);

    useEffect(() => {
      handleContentOverflow();
    }, [notification, children, handleContentOverflow]);

    return (
      <RadixPortal container={portalContainer}>
        <RadixOverlay className={cx(`${rootClassName}__overlay`)} style={{ zIndex }} />
        <RadixContent
          {...rest}
          className={classes}
          data-testid={dataTestId}
          ref={ref}
          {...(!description && { ["aria-describedby"]: undefined })}
          onPointerDownOutside={handlePointerDownOutside}
          onOpenAutoFocus={() => {
            handleContentOverflow();
            handleInitialFocus();
          }}
          onEscapeKeyDown={handleEscapeKeyDown}
          onCloseAutoFocus={onCloseAutoFocus}
          style={{ zIndex: `calc(${zIndex} + 1)` }}
        >
          <div ref={wrapperRef} className={cx(`${rootClassName}__wrapper`)}>
            {hasCloseButtonProps(props) && closeButtonAriaLabel && image && (
              <CloseButton
                aria-label={closeButtonAriaLabel}
                hasImage
                scrollbarWidth={scrollbarWidth}
                data-testid={getTestId("close-button")}
              />
            )}
            <div ref={wrapperInnerRef} className={cx(`${rootClassName}__wrapper-inner`)}>
              <div className={cx(`${rootClassName}__header`)}>
                {hasCloseButtonProps(props) && closeButtonAriaLabel && !image && (
                  <CloseButton
                    aria-label={closeButtonAriaLabel}
                    data-testid={getTestId("close-button")}
                  />
                )}
                <RadixTitle
                  asChild
                  className={cx(`${rootClassName}__title`)}
                  data-testid={getTestId("title")}
                >
                  <Heading
                    tag="h2"
                    variant="display-10"
                    tabIndex={-1}
                    className={cx(`${rootClassName}__heading`)}
                  >
                    {title}
                  </Heading>
                </RadixTitle>
              </div>
              {image && (
                <div
                  className={cx(`${rootClassName}__image-wrapper`)}
                  data-testid={getTestId("image")}
                >
                  {image}
                </div>
              )}
              <div className={cx(`${rootClassName}__body`)}>
                <div className={cx(`${rootClassName}__body-inner`)}>
                  {description && hideDescription && (
                    <VisuallyHidden asChild>
                      <RadixDescription data-testid={getTestId("description")}>
                        {description}
                      </RadixDescription>
                    </VisuallyHidden>
                  )}
                  {description && !hideDescription && (
                    <RadixDescription
                      asChild
                      className={cx(`${rootClassName}__description`)}
                      data-testid={getTestId("description")}
                    >
                      <Paragraph variant="paragraph-100">{description}</Paragraph>
                    </RadixDescription>
                  )}
                  <div>{children}</div>
                  {!stickyButtons && <ModalActions actions={actions} notification={notification} />}
                </div>
              </div>
            </div>
          </div>

          {stickyButtons && <ModalActions actions={actions} notification={notification} />}
        </RadixContent>
      </RadixPortal>
    );
  }
);

const CloseButton = ({
  ["aria-label"]: ariaLabel,
  hasImage,
  scrollbarWidth,
  ["data-testid"]: dataTestId,
}: {
  ["aria-label"]: string;
  hasImage?: boolean;
  scrollbarWidth?: number;
  ["data-testid"]?: string;
}) => (
  <RadixClose asChild>
    <Button
      variant={hasImage ? "primary" : "tertiary-purple"}
      negative={hasImage}
      size="sm"
      iconOnly
      aria-label={ariaLabel}
      className={cx(`${rootClassName}__close-button`)}
      style={{ right: `calc(var(--purpur-spacing-100) + ${scrollbarWidth || 0}px)` }}
      data-testid={dataTestId}
    >
      <IconClose />
    </Button>
  </RadixClose>
);

export const MAX_NUMBER_OF_ACTIONS = 3;

export const ModalActions = ({
  actions,
  notification,
}: Pick<ModalContentProps, "actions" | "notification">) => {
  const notificationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (notification) {
      notificationRef?.current?.scrollIntoView?.(false);
    }
  }, [notification]);

  return actions ? (
    <div className={cx(`${rootClassName}__actions`)}>
      <div className={cx(`${rootClassName}__actions-buttons`)} data-testid="modal actions">
        {actions}
      </div>
      {notification && (
        <div className={cx(`${rootClassName}__notification`)} ref={notificationRef}>
          {notification}
        </div>
      )}
      <span className={cx(`${rootClassName}__actions-separator`)} />
    </div>
  ) : null;
};
